<template>
    <!-- Mobile Receipt Container -->
    <div class="mobile-receipt-container">
        <!-- Top Action Bar -->
        <div class="mobile-top-bar">
            <button class="mobile-icon-btn" @click.prevent="closeModal" aria-label="Close">
                <i class="fas fa-times"></i>
            </button>

        </div>

        <!-- Main White Rounded Card -->
        <div class="mobile-receipt-card">
            <!-- Green/Dynamic Status Icon -->
            <div class="mobile-success-icon-wrapper">
                <div class="mobile-success-circle" :class="mobileStatusCircleClass">
                    <i :class="mobileStatusIconClass"></i>
                </div>
            </div>

            <!-- Title & Subtitle -->
            <h3 class="mobile-status-title">{{ mobileStatusTitle }}</h3>
            <div class="mobile-merchant-info">
                <div class="mobile-branch-name">{{ item?.branch_name || item?.corporate_name || 'Merchant' }}</div>
                <div class="mobile-merchant-sub">{{ item?.merchant_name || item?.branch_name || '-' }}</div>
            </div>

            <!-- Dashed Divider -->
            <div class="mobile-dashed-divider"></div>

            <!-- Transaction Details Info -->
            <div class="mobile-info-list">
                <div class="mobile-info-row">
                    <span class="mobile-info-label">Transaction Code</span>
                    <span class="mobile-info-value fw-bold">{{ item.transaction_code || '-' }}</span>
                </div>
                <div class="mobile-info-row">
                    <span class="mobile-info-label">Date</span>
                    <span class="mobile-info-value fw-bold">{{ formatDateOnly(item.created_at) }}</span>
                </div>
               
                <div class="mobile-info-row">
                    <span class="mobile-info-label">Reference Code</span>
                    <span class="mobile-info-value fw-bold">{{ item.reference_code || '-' }}</span>
                </div>
                <div class="mobile-info-row align-items-center">
                    <span class="mobile-info-label">Type</span>
                    <span class="mobile-info-value fw-bold">{{ transactionTypeDisplay }}</span>
                </div>
            </div>

            <!-- Dashed Divider below Type -->
            <div class="mobile-dashed-divider"></div>

            <!-- Details Table/List -->
            <div class="mobile-details-section">
                <div class="mobile-details-header">
                    <span class="mobile-details-label">Details</span>
                    <span class="mobile-details-qty-header">{{ qtyHeaderDisplay }}</span>
                </div>
                <div class="mobile-details-list">
                    <div v-for="(detail, index) in displayedMobileDetails" :key="index" class="mobile-detail-row">
                        <span class="mobile-detail-name">{{ detail.name }}</span>
                        <span class="mobile-detail-qty fw-bold">{{ detail.qty }}</span>
                    </div>
                </div>
                <div v-if="getFormattedDetailsList().length > 3" class="mobile-details-toggle-wrapper">
                    <button @click.prevent="isMobileDetailsExpanded = !isMobileDetailsExpanded" type="button" class="mobile-details-toggle-btn">
                        <span>{{ isMobileDetailsExpanded ? 'Show Less' : 'Show All' }}</span>
                        <i :class="isMobileDetailsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                    </button>
                </div>
                <div v-if="!isPointTransaction && item.point > 0" class="mobile-detail-row" style="margin-top: 8px; border-top: 1px solid rgba(255,255,255,0.2); padding-top: 8px;">
                    <span class="mobile-detail-name" style="font-weight: 600;">{{ pointEarnedLabel }}</span>
                    <span class="mobile-detail-qty fw-bold" :style="pointEarnedStyle">{{ pointEarnedDisplay }}</span>
                </div>
            </div>

           <!-- Subtotal & Discount -->
            <div v-if="!isPointTransaction && !hasVoucherDiscount" class="mobile-total-row" style="margin-bottom: 10px; border-top: 1px dashed rgba(255, 255, 255, 0.3); padding-top: 15px;">
                <span class="mobile-total-label">Subtotal</span>
                <span class="mobile-total-value" style="font-size: 15px; font-weight: 600;">{{ subtotalDisplay }}</span>
            </div>
            <div v-if="hasVoucherDiscount" class="mobile-total-row" style="margin-bottom: 10px; border-top: 1px dashed rgba(255, 255, 255, 0.3); padding-top: 15px;">
                <span class="mobile-total-label">Subtotal</span>
                <span class="mobile-total-value" style="font-size: 15px; font-weight: 600;">{{ displayBeforeVoucher }}</span>
            </div>
            <div v-if="hasVoucherDiscount" style="display: flex; justify-content: space-between; align-items: center;">
                <span class="mobile-total-label" style="font-size: 13px; opacity: 0.9;">Voucher Discount</span>
                <span class="mobile-total-value text-danger" style="font-size: 15px; font-weight: 700;">- {{ displayVoucherDiscount }}</span>
            </div>
            <div v-if="showTaxRow" class="mobile-detail-row" style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 8px;">
                <span class="mobile-detail-name" style="font-weight: 600;">Tax ({{ percentageFormat(displayTaxCharge) }})</span>
                <span class="mobile-detail-qty fw-bold" style="font-size: 15px; font-weight: 600;">{{ formattedTaxValue }}</span>
            </div>
            <div v-if="showServiceRow" class="mobile-detail-row" style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 8px;">
                <span class="mobile-detail-name" style="font-weight: 600;">Service Charge ({{ percentageFormat(displayServiceCharge) }})</span>
                <span class="mobile-detail-qty fw-bold" style="font-size: 15px; font-weight: 600;">{{ formattedServiceValue }}</span>
            </div>
            <!-- Total Row -->
            <div class="mobile-total-row" :style="hasVoucherDiscount ? 'margin-top: 8px; border-top: none; padding-top: 5px;' : ''">
                <span class="mobile-total-label">
                    {{ totalLabel }}
                    <template v-if="totalLabelNote"><br>{{ totalLabelNote }}</template>
                </span>
                <span class="mobile-total-value">{{ totalValue }}</span>
            </div>

            <button class="mobile-btn-get-pdf" @click.prevent="printReceipt" type="button" :disabled="isPdfLoading">
                <span v-if="isPdfLoading" class="mobile-pdf-spinner me-2"></span>
                <i v-else class="fas fa-download me-2"></i>
                {{ isPdfLoading ? 'Downloading...' : 'Download Receipt' }}
            </button>
        </div>

        <!-- Thank You Message & Back to Home Button -->
        <div class="mobile-footer-section">
            <p class="mobile-thank-you-text">
                Thank you for your transaction. Please present this copy to the merchant.
            </p>
            <button class="mobile-btn-back-home" @click.prevent="closeModal" type="button">
                Back to Home
            </button>
        </div>
    </div>
</template>

<script>
const toNumber = (value) => parseFloat(value) || 0;
export default {
    name: 'MemberTransactionMobile',
    props: {
        item: {
            type: Object,
            required: true,
            default: () => ({})
        },
        isPdfLoading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isMobileDetailsExpanded: false,
        };
    },
    methods: {
        closeModal(e) {
            this.$emit('closeModal', e);
        },
        printReceipt() {
            this.$emit('printReceipt');
        },

        priceFormat(value) {
            if (value == null || value === '') {
                return '-';
            }

            let isNegative = false;

            if (typeof value === 'number') {
                if (value < 0) {
                    isNegative = true;
                    value = Math.abs(value);
                }
            } else if (typeof value === 'string') {
                if (value.trim().startsWith('-')) {
                    isNegative = true;
                    value = value.trim().slice(1);
                }
            }

            let formatted = value.toString().replace(/[^0-9.,]/g, '');
            let parts = formatted.split(/[.,]/);
            let mainNumber = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            let decimalPart = parts[1] && parts[1] !== '00' ? ',' + parts[1] : '';

            const finalResult = (isNegative ? '-' : '') + mainNumber + decimalPart;
            return finalResult;
        },
        percentageFormat(value) {
            if (value == null || value === '') {
                return '-';
            }

            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) {
                return '-';
            }

            return `${numericValue.toLocaleString('id-ID', {
                maximumFractionDigits: 2,
            })}%`;
        },
        formatDateOnly(dateStr) {
            if (!dateStr) return '-';
            try {
                const safeDateStr = String(dateStr).replace(' ', 'T');
                const d = new Date(safeDateStr);
                if (isNaN(d.getTime())) {
                    const parts = String(dateStr).split(' ');
                    return parts[0] || String(dateStr);
                }
                return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
            } catch (e) {
                return String(dateStr);
            }
        },
        getFormattedDetailsList() {
            if (!this.item.detail_name) {
                return [{ name: '-', qty: String(this.displayQty !== undefined && this.displayQty !== null ? this.displayQty : '1') }];
            }
            
            let detailStr = String(this.item.detail_name);
            let details = detailStr.includes('$')
                ? detailStr.split('$').filter(d => d && String(d).trim() !== '')
                : [detailStr];
                
            let qtyStr = !this.isPointTransaction && this.item.qty !== undefined && this.item.qty !== null
                ? String(this.item.qty)
                : String(this.displayQty !== undefined && this.displayQty !== null ? this.displayQty : '1');
                
            let qtys = qtyStr.includes('$')
                ? qtyStr.split('$').filter(q => q && String(q).trim() !== '')
                : [qtyStr];

            return details.map((name, idx) => {
                const q = qtys[idx] !== undefined ? qtys[idx] : (qtys[0] !== undefined ? qtys[0] : '1');
                return {
                    name: String(name).trim() || '-',
                    qty: String(q).trim() || '1'
                };
            });
        }
    },
    computed: {
        displayedMobileDetails() {
            const list = this.getFormattedDetailsList();
            if (this.isMobileDetailsExpanded || list.length <= 3) {
                return list;
            }
            return list.slice(0, 3);
        },
        isPointTransaction() {
            return (!this.item.transaction_value || this.item.transaction_value == 0) && this.item.point !== null && this.item.point !== undefined;
        },
        isPending() {
            return Number(this.item.is_pending) === 1 || this.item.is_pending === true || this.item.is_pending === 'true';
        },
        pointEarnedLabel() {
            return this.isPending ? 'Points Pending' : 'Points Earned';
        },
        pointEarnedDisplay() {
            return this.isPending ? `${this.item.point} POIN` : `+ ${this.item.point} POIN`;
        },
        pointEarnedStyle() {
            if (this.isPending) {
                return { color: '#6B7280' };
            }
            return { color: '#00AA00' };
        },
        displayQty() {
            if (this.isPointTransaction) return this.item.point;
            return this.item.qty;
        },
        displayAmount() {
            if (this.isPointTransaction) return `${this.item.point} POIN`;
            if (this.hasVoucherDiscount && this.item.amount_after_voucher !== undefined && this.item.amount_after_voucher !== null) {
                return this.priceFormat(this.item.amount_after_voucher);
            }
            return this.priceFormat(this.item.transaction_value);
        },
        displayBeforeVoucher(){
            return this.priceFormat(this.item.transaction_value);
        },
        hasVoucherDiscount() {
            return !this.isPointTransaction && this.item.voucher_discount && parseFloat(this.item.voucher_discount) > 0;
        },
        displayGrossAmount() {
            return this.priceFormat(this.item.transaction_value);
        },
        displayVoucherDiscount() {
            return this.priceFormat(this.item.voucher_discount);
        },
        displayBeforeTaxService() {
            return this.priceFormat(this.item.before_tax_service);
        },
        displayAfterTaxService() {
            return this.priceFormat(this.item.after_tax_service);
        },
        /* ---------- Subtotal ---------- */

        // Tax dan service sama-sama punya nilai > 0
        // kalau hanya salah satu yang ada, subtotal jatuh ke displayAmount.
        hasTaxAndServiceValue() {
            return toNumber(this.displayTaxValue) > 0 && toNumber(this.displayServiceValue) > 0;
        },

        subtotalDisplay() {
            if (this.hasVoucherDiscount) return this.displayBeforeVoucher;
            return this.hasTaxAndServiceValue ? this.displayBeforeTaxService : this.displayAmount;
        },

        /* ---------- Tax & Service ---------- */

        // Satu aturan untuk tax dan service
        // Tampil jika: ada voucher, atau amount_after_voucher kosong, atau lebih kecil dari transaction_value.
        showTaxServiceBreakdown() {
            if (this.hasVoucherDiscount) return true;

            const afterVoucher = this.item.amount_after_voucher;            

            return afterVoucher < this.item.transaction_value;
        },

        displayTaxCharge() {
            return this.showTaxServiceBreakdown ? this.item.tax_charge : 0;
        },
        displayServiceCharge() {
            return this.showTaxServiceBreakdown ? this.item.service_charge : 0;
        },

        showTaxRow() {
            return toNumber(this.displayTaxCharge) > 0;
        },
        showServiceRow() {
            return toNumber(this.displayServiceCharge) > 0;
        },

        formattedTaxValue() {
            return this.displayTaxValue ? this.priceFormat(this.displayTaxValue) : 0;
        },
        formattedServiceValue() {
            return this.displayServiceValue ? this.priceFormat(this.displayServiceValue) : 0;
        },

        /* ---------- Total ---------- */

        totalLabel() {
            return this.isPointTransaction ? 'Total Processed' : 'Total Payment';
        },

        totalLabelNote() {            
            return this.isTaxServiceIncluded ? '(Tax & Service Included)' : '';
        },

        isTaxServiceIncluded() {
            if (this.isPointTransaction) return false;

            const { tax_value, service_value, before_tax_service, after_tax_service, transaction_value } = this.item;

            const hasTaxAndService = toNumber(tax_value) > 0 && toNumber(service_value) > 0;
            const beforeDiffersFromSubtotal = toNumber(before_tax_service) !== toNumber(transaction_value);
            const afterWithinSubtotal = toNumber(after_tax_service) <= toNumber(transaction_value);
            const afterWithDiscount = this.hasVoucherDiscount && afterWithinSubtotal ? true : !this.hasVoucherDiscount && afterWithinSubtotal ? true : false;
            return hasTaxAndService && beforeDiffersFromSubtotal && afterWithDiscount;
        },

        totalValue() {
            return this.isPointTransaction ? this.displayAmount : this.displayTotalPayment;
        },

        // MEM 1115
        displayTotalPayment() {
            const {
                tax_charge,
                service_charge,
                after_tax_service,
                amount_after_voucher,
                transaction_value,
            } = this.item;

            const hasTaxAndService = toNumber(tax_charge) > 0 && toNumber(service_charge) > 0;

            if (hasTaxAndService) {
                
                if (after_tax_service != null) {
                    return this.priceFormat(after_tax_service);
                }
                if (this.hasVoucherDiscount && amount_after_voucher != null) {
                    return this.priceFormat(amount_after_voucher);
                }
            }else if (this.hasVoucherDiscount && amount_after_voucher != null) {
                return this.priceFormat(amount_after_voucher);
            }

            return this.priceFormat(transaction_value);
        },
         displayTaxValue() {
            return this.item.tax_value;
        },
        displayServiceValue() {
            return this.item.service_value;
        },
        transactionTypeDisplay() {
            return this.dynamicTransactionTitle;
        },
        mobileStatusTitle() {
            return this.dynamicTransactionTitle;
        },
        mobileStatusCircleClass() {
            return '';
        },
        mobileStatusIconClass() {
            return 'fas fa-receipt';
        },
        qtyHeaderDisplay() {
            if (this.isPointTransaction || (this.item && this.item.type !== 'transaction')) {
                return 'POINTS';
            }
            return 'QTY';
        },
        dynamicTransactionTitle() {
            if (!this.item) return 'Transaction';

            const typeRaw = this.item.type || '';
            const type = typeRaw ? typeRaw.charAt(0).toUpperCase() + typeRaw.slice(1) : '';
            const detailRaw = this.item.detail_name;

            if (typeRaw.toLowerCase() === 'gift') {
                return detailRaw ? `Gift - ${detailRaw}` : 'Gift Voucher';
            }

            const checkDetail = (detailRaw || '').toLowerCase();
            const checkCode = (this.item.transaction_code || this.item.code || '');
            const typeLower = typeRaw.toLowerCase();

            if (typeLower === 'expiration' || checkDetail.includes('expired') || checkDetail.includes('expiration') || checkCode.startsWith('EXP-')) {
                return 'Expiration';
            }

            if (detailRaw === 'Top Up') {
                return detailRaw;
            }

            if (detailRaw === 'Deduction Point' || detailRaw == null) {
                return 'Deduction Point';
            }

            if (typeLower === 'transaction' || typeLower === 'redeem' || typeLower === 'cancel') {
                return type || 'Transaction';
            }

            const detailParts = (detailRaw || '')
                .split('$')
                .map(part => part.trim())
                .filter(Boolean);

            if (!detailParts.length) {
                return type ? `${type} - Deduction Point` : 'Deduction Point';
            }

            const [firstDetail, ...restDetails] = detailParts;
            const extraCount = restDetails.length;
            const detailText = extraCount ? `${firstDetail} +${extraCount}` : firstDetail;

            return type ? `${type} - ${detailText}` : detailText;
        }
    }
};
</script>

<style scoped>
.mobile-receipt-container {
    display: none;
}

/* Mobile Top Bar (Base Styles) */
.mobile-top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}
.mobile-icon-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    color: #ffffff;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s ease;
}
.mobile-icon-btn:hover, .mobile-icon-btn:active {
    background: rgba(255, 255, 255, 0.35);
}

/* Main White Card (Base Styles) */
.mobile-receipt-card {
    background: 
        radial-gradient(circle at 0 199px, transparent 14px, white 14px) left top,
        radial-gradient(circle at 100% 199px, transparent 14px, white 14px) right top;
    background-size: 51% 100%;
    background-repeat: no-repeat;
    border-radius: 24px;
    padding: 24px 20px;
    width: 100%;
    box-sizing: border-box;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
    position: relative;
    overflow: hidden;
}

/* Green Checkmark Circle */
.mobile-success-icon-wrapper {
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
}
.mobile-success-circle {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: #e6f7ef;
    color: #10b981;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
}
.mobile-success-circle--cancel {
    background-color: #feece8 !important;
    color: #ef4444 !important;
}
.mobile-success-circle--expiration {
    background-color: #feece8 !important;
    color: #ef4444 !important;
}
.mobile-success-circle--gift {
    background-color: #f3e8ff !important;
    color: #9333ea !important;
}

/* Title & Subtitle */
.mobile-status-title {
    font-size: 22px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 6px 0;
    text-align: center;
}
.mobile-merchant-info {
    text-align: center;
    margin-bottom: 20px;
}
.mobile-branch-name {
    font-size: 15px;
    color: #6b7280;
    font-weight: 500;
}
.mobile-merchant-sub {
    font-size: 13px;
    color: #9ca3af;
}

/* Dashed Divider */
.mobile-dashed-divider {
    width: 100%;
    border-top: 2px dashed #e5e7eb;
    margin: 4px 0 18px 0;
}

/* Details Info Rows */
.mobile-info-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
}
.mobile-info-row {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    font-size: 14px;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
}
.mobile-info-label {
    color: #9ca3af;
    font-weight: 500;
    flex-shrink: 0;
}
.mobile-info-value {
    color: #1f2937;
    font-weight: 600;
    text-align: right !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    flex: 1;
}

/* Details Section */
.mobile-details-section {
    width: 100%;
    margin-bottom: 20px;
}
.mobile-details-header {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #9ca3af;
    font-weight: 500;
    margin-bottom: 10px;
}
.mobile-details-qty-header {
    font-weight: 700;
    color: #1f2937;
}
.mobile-details-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.mobile-details-toggle-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 12px;
    margin-bottom: 4px;
}
.mobile-details-toggle-btn {
    background: transparent !important;
    border: none !important;
    padding: 4px 8px !important;
    color: #9ca3af;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    white-space: nowrap;
    transition: color 0.2s ease;
}
.mobile-details-toggle-btn i {
    font-size: 10px;
    color: #9ca3af;
    transition: transform 0.2s ease, color 0.2s ease;
}
.mobile-details-toggle-btn:hover {
    color: #6b7280;
}
.mobile-details-toggle-btn:hover i {
    color: #6b7280;
}
.mobile-detail-row {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    font-size: 14px;
    color: #4b5563;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
}
.mobile-detail-name {
    flex: 1;
    padding-right: 12px;
    word-break: break-word;
    overflow-wrap: anywhere;
}
.mobile-detail-qty {
    color: #1f2937;
    font-weight: 700;
    flex-shrink: 0;
}

/* Total Row */
.mobile-total-row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    border-top: 1px dashed #e5e7eb;
    margin-bottom: 24px;
}
.mobile-total-label {
    font-size: 16px;
    color: #4b5563;
    font-weight: 600;
}
.mobile-total-value {
    font-size: 22px;
    font-weight: 800;
    color: #111827;
}

/* Download Receipt Button */
.mobile-btn-get-pdf {
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    padding: 12px 20px;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    border-radius: 14px;
    color: #374151;
    font-size: 15px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}
.mobile-btn-get-pdf:hover, .mobile-btn-get-pdf:active {
    background: #f9fafb;
    border-color: #d1d5db;
}

/* Footer Section on Primary Background */
.mobile-footer-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 8px;
    padding-top: 10px;
    padding-bottom: 24px;
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
}
.mobile-thank-you-text {
    color: rgba(255, 255, 255, 0.92);
    font-size: 13px;
    text-align: center;
    line-height: 1.5;
    margin: 0;
    padding: 0 10px;
}
.mobile-btn-back-home {
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    padding: 16px;
    background: #ffffff;
    border: none;
    border-radius: 16px;
    color: var(--primary-color);
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}
.mobile-btn-back-home:hover, .mobile-btn-back-home:active {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Modal Entry Animation */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
@keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}
:deep(.modal.modal-fullscreen),
:deep(.modal-backdrop) {
    animation: fadeIn 0.2s ease-out !important;
}
:deep(.modal.modal-fullscreen .modal-content) {
    animation: slideUp 0.3s ease-out, fadeIn 0.3s ease-out !important;
}

/* Small Devices (Tablets, Mobile Phones <= 768px) */
@media (max-width: 768px) {
    .mobile-receipt-container {
        display: flex !important;
        flex-direction: column;
        padding: 16px 16px 32px 16px;
        min-height: 100%;
        height: auto !important;
        width: 100%;
        max-width: 480px;
        margin: 0 auto;
        box-sizing: border-box;
        color: #333;
    }

    /* Override ORecepient modal hierarchy to guarantee scrollable flex viewport */
    :deep(.modal-fullscreen) {
        display: flex !important;
        align-items: stretch !important;
        justify-content: stretch !important;
        overflow: hidden !important;
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        padding: 0 !important;
        margin: 0 !important;
        animation: fadeIn 0.2s ease-out !important;
    }
    :deep(.modal-fullscreen .modal-dialog) {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow: hidden !important;
    }
    :deep(.modal-fullscreen .modal-content) {
        background-color: var(--primary-color) !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        width: 100% !important;
        height: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow: hidden !important;
        animation: slideUp 0.3s ease-out, fadeIn 0.3s ease-out !important;
    }
    :deep(.modal-fullscreen .modal-body) {
        padding: 0 !important;
        margin: 0 !important;
        display: block !important;
        width: 100% !important;
        flex: 1 1 0% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch !important;
        background-color: var(--primary-color) !important;
    }
    :deep(.modal-header),
    :deep(.modal-footer) {
        display: none !important;
    }
}

/* Extra Small Devices (Standard Mobile Phones <= 576px) */
@media (max-width: 576px) {
    .mobile-receipt-container {
        padding: 14px 14px 44px 14px;
    }
    .mobile-top-bar {
        margin-bottom: 14px;
    }
    .mobile-receipt-card {
        padding: 20px 16px;
        border-radius: 20px;
        margin-bottom: 16px;
        background: 
            radial-gradient(circle at 0 186px, transparent 14px, white 14px) left top,
            radial-gradient(circle at 100% 186px, transparent 14px, white 14px) right top;
        background-size: 51% 100%;
        background-repeat: no-repeat;
    }
    .mobile-status-title {
        font-size: 20px;
    }
    .mobile-merchant-info {
        margin-bottom: 16px;
    }
    .mobile-branch-name {
        font-size: 14px;
    }
    .mobile-total-value {
        font-size: 20px;
    }
    .mobile-info-row,
    .mobile-detail-row {
        font-size: 13.5px;
    }
}

/* Compact Mobile Phones (<= 480px) */
@media (max-width: 480px) {
    .mobile-receipt-container {
        padding: 12px 12px 40px 12px;
    }
    .mobile-top-bar {
        margin-bottom: 12px;
    }
    .mobile-icon-btn {
        width: 36px;
        height: 36px;
        font-size: 14px;
    }
    .mobile-receipt-card {
        padding: 18px 14px;
        border-radius: 18px;
        margin-bottom: 14px;
        background: 
            radial-gradient(circle at 0 169px, transparent 13px, white 13px) left top,
            radial-gradient(circle at 100% 169px, transparent 13px, white 13px) right top;
        background-size: 51% 100%;
        background-repeat: no-repeat;
    }
    .mobile-success-circle {
        width: 54px;
        height: 54px;
        font-size: 24px;
    }
    .mobile-status-title {
        font-size: 18px;
    }
    .mobile-branch-name {
        font-size: 14px;
    }
    .mobile-merchant-sub {
        font-size: 12px;
    }
    .mobile-info-list {
        gap: 10px;
        margin-bottom: 16px;
    }
    .mobile-info-row,
    .mobile-detail-row {
        font-size: 13px;
        gap: 8px;
    }
    .mobile-details-section {
        margin-bottom: 16px;
    }
    .mobile-total-row {
        padding-top: 14px;
        margin-bottom: 18px;
    }
    .mobile-total-label {
        font-size: 15px;
    }
    .mobile-total-value {
        font-size: 18px;
    }
    .mobile-btn-get-pdf {
        padding: 10px 16px;
        font-size: 14px;
        border-radius: 12px;
    }
    .mobile-footer-section {
        gap: 14px;
        padding-top: 8px;
        padding-bottom: 24px;
    }
    .mobile-thank-you-text {
        font-size: 12px;
        padding: 0 4px;
    }
    .mobile-btn-back-home {
        padding: 14px;
        font-size: 15px;
        border-radius: 14px;
    }
}

/* Small Mobile Screens / Foldables (<= 380px) */
@media (max-width: 380px) {
    .mobile-receipt-container {
        padding: 10px 10px 36px 10px;
    }
    .mobile-receipt-card {
        padding: 14px 12px;
        border-radius: 16px;
        background: 
            radial-gradient(circle at 0 147px, transparent 12px, white 12px) left top,
            radial-gradient(circle at 100% 147px, transparent 12px, white 12px) right top;
        background-size: 51% 100%;
        background-repeat: no-repeat;
    }
    .mobile-success-circle {
        width: 46px;
        height: 46px;
        font-size: 20px;
    }
    .mobile-status-title {
        font-size: 16px;
    }
    .mobile-branch-name {
        font-size: 13px;
    }
    .mobile-info-row,
    .mobile-detail-row {
        font-size: 12.5px;
        gap: 6px;
    }
    .mobile-total-label {
        font-size: 14px;
    }
    .mobile-total-value {
        font-size: 16px;
    }
    .mobile-btn-get-pdf,
    .mobile-btn-back-home {
        padding: 12px;
        font-size: 14px;
    }
}

/* Landscape Orientation & Short Viewports (<= 650px height) */
@media (max-height: 650px) and (max-width: 768px) and (orientation: landscape) {
    .mobile-receipt-card {
        padding: 16px;
        background: 
            radial-gradient(circle at 0 160px, transparent 13px, white 13px) left top,
            radial-gradient(circle at 100% 160px, transparent 13px, white 13px) right top;
        background-size: 51% 100%;
        background-repeat: no-repeat;
    }
    .mobile-success-icon-wrapper {
        margin-bottom: 8px;
    }
    .mobile-success-circle {
        width: 44px;
        height: 44px;
        font-size: 20px;
    }
    .mobile-status-title {
        font-size: 18px;
    }
    .mobile-merchant-info {
        margin-bottom: 12px;
    }
}
</style>
